import React, { createContext, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { Base64 } from "js-base64";

import { clientKeys } from "hooks/api/query-keys";

import { getClient } from "generated/api/client";
import type {
  ClientDTO,
  ClientOverviewDTO,
  ClientPatchRequest,
  CreateClientVm,
  GetAllClientsParams,
  PagedModelClientDTO,
} from "generated/schemas";

import { PaginatedRequestOptions, PaginatedResponse } from "types/pagination";
import { mapPage } from "utilities/pagination";

const clientApi = getClient();

/** Alias for `ClientDTO`; kept for the project-wide `ClientAttributes` import path. */
export type ClientAttributes = ClientDTO;

/**
 * Loose write-body accepted by client create/replace mutations. Mirrors the
 * legacy `ClientAttributes`-shaped payload the existing forms construct.
 *
 * Mutations cast this body to the strict generated DTO (`CreateClientVm` for
 * POST, `ClientDTO` for PUT) before handing it to the Orval client, and
 * Base64-encode WAF-sensitive URL fields via `encodeClientPayload`.
 */
export type ClientWriteBody = {
  id?: string;
  name: string;
  clientCode: string;
  collegeUrl?: string;
  privacyPolicyUrl?: string;
  iterableProjectId?: string;
  apolloDomainUrl?: string;
  iterableApiKey?: string;
  tealiumProfileCode?: string;
};

/** Loose patch body; same rationale as `ClientWriteBody`. */
export type ClientPatchBody = {
  name?: string;
  clientCode?: string;
  collegeUrl?: string;
  privacyPolicyUrl?: string;
  iterableProjectId?: string;
  apolloDomainUrl?: string;
  overview?: ClientOverviewDTO;
};

const DEFAULT_CLIENTS_SORT = ["name,asc"];

export type ClientListParams = PaginatedRequestOptions;

type UseQueryHookOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, Error, TData>,
  "queryKey" | "queryFn"
>;

/**
 * WAF workaround inherited from the legacy `Client` model: certain URL fields
 * are Base64-encoded before POST/PUT to bypass historical firewall rules. The
 * backend (`ClientResource.createClient`/`updateClient`) decodes them safely
 * via `Base64Utils.decodeBase64String`, which falls back to the original value
 * when the input is not valid Base64. PATCH does NOT decode on the backend, so
 * encoding is only applied to create/replace paths.
 *
 * See AGENTS.md "Known Pitfalls" for context.
 */
const URL_FIELDS_TO_ENCODE = ["collegeUrl", "privacyPolicyUrl", "apolloDomainUrl"] as const;

function encodeClientPayload<T extends Record<string, unknown>>(payload: T): T {
  const encoded: Record<string, unknown> = { ...payload };
  for (const field of URL_FIELDS_TO_ENCODE) {
    const value = encoded[field];
    if (typeof value === "string" && value.length > 0) {
      encoded[field] = Base64.encode(value);
    }
  }
  return encoded as T;
}

function normalizePageResponse(page: PagedModelClientDTO): PaginatedResponse<ClientAttributes> {
  return mapPage(page, (item) => item as ClientAttributes);
}

function buildClientListParams(listParams: PaginatedRequestOptions): GetAllClientsParams {
  const { page, size, sort, unpaged } = listParams;
  const sortParam = sort ? [sort] : DEFAULT_CLIENTS_SORT;

  if (unpaged || size === undefined) {
    return { unpaged: true, sort: sortParam };
  }

  return {
    page: page ?? 0,
    size,
    sort: sortParam,
  };
}

const CurrentClientContext = createContext<ClientAttributes | null>(null);

export function CurrentClientProvider({
  client,
  children,
}: React.PropsWithChildren<{ client: ClientAttributes }>): React.ReactElement {
  return <CurrentClientContext.Provider value={client}>{children}</CurrentClientContext.Provider>;
}

export const useCurrentClientId = () => {
  const contextClient = useContext(CurrentClientContext);
  const { clientId } = useParams<{ clientId: string }>();

  return contextClient?.id ?? clientId;
};

export const useClient = <TData = ClientAttributes,>(
  id?: string,
  options?: UseQueryHookOptions<ClientAttributes, TData>,
): ReturnType<typeof useQuery<ClientAttributes, Error, TData>> => {
  return useQuery({
    queryKey: clientKeys.detail(id ?? ""),
    queryFn: async () => {
      if (!id) {
        throw new Error("Context required for useClient queryFn");
      }
      const dto = await clientApi.getClient({ id });
      return dto as ClientAttributes;
    },
    ...options,
    enabled: Boolean(id) && (options?.enabled ?? true),
  });
};

export const useClients = <TData = PaginatedResponse<ClientAttributes>,>(
  params: ClientListParams = {},
  options?: UseQueryHookOptions<PaginatedResponse<ClientAttributes>, TData>,
): ReturnType<typeof useQuery<PaginatedResponse<ClientAttributes>, Error, TData>> => {
  const apiParams = buildClientListParams(params);

  return useQuery({
    queryKey: clientKeys.list(apiParams),
    queryFn: async () => {
      const page = await clientApi.getAllClients(apiParams);
      return normalizePageResponse(page);
    },
    ...options,
  });
};

export function setClientDetailCache(queryClient: QueryClient, attributes: ClientAttributes) {
  queryClient.setQueryData(clientKeys.detail(attributes.id), attributes);
  queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
}

export const useSetClientDetailCache = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (attributes: ClientAttributes) => {
      setClientDetailCache(queryClient, attributes);
    },
    [queryClient],
  );
};

export function useOptionalCurrentClient(): ClientAttributes | null {
  const contextClient = useContext(CurrentClientContext);
  const clientId = useCurrentClientId();
  const { data } = useClient(clientId, {
    enabled: !contextClient && !!clientId,
  });

  return contextClient ?? data ?? null;
}

export function useCurrentClient(): ClientAttributes {
  const attributes = useOptionalCurrentClient();

  if (!attributes) {
    throw new Error("useCurrentClient: called before a client was cached");
  }

  return attributes;
}

export const useCreateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ClientWriteBody) =>
      clientApi.createClient(
        encodeClientPayload(data) as unknown as CreateClientVm,
      ) as Promise<ClientAttributes>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
    },
  });
};

export const useReplaceClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ClientWriteBody }) =>
      clientApi.updateClient(
        { id },
        encodeClientPayload({ ...data, id }) as unknown as ClientDTO,
      ) as Promise<ClientAttributes>,
    onSuccess: (data) => {
      setClientDetailCache(queryClient, data);
    },
  });
};

/** Backend PATCH does NOT decode Base64; payload is sent as-is. */
export const usePartiallyUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ClientPatchBody }) =>
      clientApi.partiallyUpdateClient(
        { id },
        data as unknown as ClientPatchRequest,
      ) as Promise<ClientAttributes>,
    onSuccess: (data) => {
      setClientDetailCache(queryClient, data);
    },
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => clientApi.deleteClient({ id }),
    onSuccess: (_data, variables) => {
      queryClient.removeQueries({ queryKey: clientKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
    },
  });
};

export const useSetIterableApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, iterableApiKey }: { id: string; iterableApiKey: string }) =>
      clientApi.setIterableApiKey({ id }, { iterableApiKey }) as Promise<ClientAttributes>,
    onSuccess: (data) => {
      setClientDetailCache(queryClient, data);
    },
  });
};
